import { Roles } from "@outschool/auth-shared";
import { Box } from "@outschool/backpack";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  activityApprovalPath,
  activitySchedulePath,
  clubManageListingPath,
  oneOnOneEnrollPath,
  searchPath,
  sectionCharterSchoolEnrollPath,
  sectionEnrollPath,
  selfPacedEnrollPath
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useProductViewed } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  EsaSessionType,
  getEsaState,
  getEsaWaitPeriod,
  getSessionType,
  useCurrentUserHasRole,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  ActivityEnrollableTimesCta,
  ClassDetailsMainBody,
  DefaultBelowClassDetail,
  MobileStickyFooter,
  SimilarListingsMiniTiles,
  useCDPEnrollOrSeeScheduleCTA,
  useIsActivitySavedByCurrentUser,
  useTrackClassPageImpression
} from "@outschool/ui-components-website";
import useFetchEnrollableTimes from "@outschool/ui-components-website/src/hooks/useFetchEnrollableTimes";
import { Container, Space } from "@outschool/ui-legacy-component-library";
import {
  Redirect,
  Screen,
  useNavigation,
  useScrollToElement
} from "@outschool/ui-utils";
import React, { useCallback, useEffect } from "react";
import { Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import * as Activity from "../../../shared/Activity";
import * as Env from "../../../shared/Env";
import * as User from "../../../shared/User";
import { useRandomCategoriesByFacetQuery } from "../../hooks/useRandomCategoriesByFacet";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import ActivityEnrollPage from "../../routes/ActivityEnrollPage";
import EnrollmentMeetingPurchasePage, {
  PrepayType
} from "../../routes/EnrollmentMeetingPurchasePage";
import TutorEnrollButtonAndModal from "../../routes/LearningPlanner/enrollmentFlow/TutorEnrollButtonAndModal";
import { useAppState } from "../../stores/AppStateProvider";
import ActivityStatusLabel from "../ActivityStatusLabel";
import { ClassScheduleRequestModal } from "../ClassScheduleRequest";
import SimpleLayout from "../layouts/SimpleLayout";
import Link from "../Link";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import { ReviewRespondButton } from "../reviews/ReviewRespondButton";
import { FollowButton } from "../saving/FollowButton";
import ActivityMetaTags from "../sharing/ActivityMetaTags";
import Toolbar from "../Toolbar";
import ChooseATimeOrClassScheduleRequestButton from "./ChooseATimeOrClassScheduleRequestButton";
import { MemberPlanDiscountTooltip } from "./MemberPlanDiscountTooltip";
import { MobileBelowClassDetail } from "./MobileBelowClassDetail";
import { SectionCallToAction } from "./SectionCallToActionButtons";

const ActivityEmailPreview = React.lazy(
  () =>
    import(
      // @ts-ignore
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityEmailPreview.jsx"
    )
);
const ActivityCharterSchoolEnrollPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityCharterSchoolEnrollPage"
    )
);
const ActivityWaitlistPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityWaitlistPage"
    )
);
const ActivityClassWalletEnrollPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityClassWalletEnrollPage"
    )
);

interface NotEnrolledViewProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  showDetails: boolean;
}

export default function NotEnrolledView(props: NotEnrolledViewProps) {
  const { currentUser, userTimeZone } = useSession();
  const isClassWalletSession = getSessionType() === EsaSessionType.CW;
  const { path } = useRouteMatch() ?? {};
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation(
    "client\\components\\detailPage\\NotEnrolledView"
  );
  const navigate = useNavigation();
  const ctaText = useCDPEnrollOrSeeScheduleCTA({
    activity: props.activity
  });
  const { activity, leader, showDetails } = props;

  const isOrganizationEnrollment = isOrganizationAdminWhoCanEnroll(currentUser);
  const canManage =
    Boolean(activity) && Activity.canManage(activity, currentUser);
  const isActivitySavedByCurrentUser =
    useIsActivitySavedByCurrentUser(activity);
  const appState = useAppState();
  const canApprove = useCurrentUserHasRole(Roles.ClassApproval);
  const location = useLocation();
  const querySectionUid = location.query && location.query.sectionUid;
  const isPrepayMeetings =
    location.query?.isPrepayMeetings === "true"
      ? true
      : location.query?.isPrepayMeetings === "false"
      ? false
      : undefined;
  const isOwnerOrAdmin =
    Activity.isOwner(activity, currentUser) || User.isAdmin(currentUser);
  const isRestricted = Boolean(activity?.isRestricted) && !isOwnerOrAdmin;
  const sectionsRef = React.useRef(null);
  const otherListingsRef = React.useRef(null);
  const moreLikeThisRef = React.useRef(null);
  const viewAvailabilityRef = React.useRef(null);
  const viewTheTeacherRef = React.useRef(null);

  const [openClassScheduleRequest, setOpenClassScheduleRequest] =
    React.useState(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
    React.useState(false);

  const scrollToElement = useScrollToElement();

  const trackProductViewed = useProductViewed();
  React.useEffect(() => {
    trackProductViewed({
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; upsel... Remove this comment to see the full error message
      activity,
      leader
    });

    const openRequestTimeModal =
      new URLSearchParams(window.location.search).get("openCSR") === "true";
    if (openRequestTimeModal && !isAvailabilityModalOpen) {
      setOpenClassScheduleRequest(openRequestTimeModal);
    }
  }, [
    activity,
    leader,
    trackProductViewed,
    navigate,
    isAvailabilityModalOpen,
    location
  ]);
  const { enterLoginFlow } = useLoginFlowContext();

  const onClickSeeSchedule = useCallback(() => {
    if (isMobile) {
      if (activity.isSelfPaced) {
        enterLoginFlow({
          authTrigger: AuthTrigger.ENROLL,
          skipLearners: true,
          newUrl: selfPacedEnrollPath(activity)
        });
      } else {
        setIsAvailabilityModalOpen(true);
      }
    } else if (sectionsRef.current) {
      scrollToElement(sectionsRef.current);
    }
  }, [sectionsRef, scrollToElement, isMobile, activity, enterLoginFlow]);

  const onClickViewAvailability = useCallback(() => {
    if (isMobile) {
      if (activity.isSelfPaced) {
        enterLoginFlow({
          authTrigger: AuthTrigger.ENROLL,
          skipLearners: true,
          newUrl: selfPacedEnrollPath(activity)
        });
      } else {
        setIsAvailabilityModalOpen(true);
      }
    } else if (viewAvailabilityRef.current) {
      scrollToElement(viewAvailabilityRef.current);
    }
  }, [
    viewAvailabilityRef,
    scrollToElement,
    isMobile,
    activity,
    enterLoginFlow
  ]);

  const onClickMoreLikeThis = React.useCallback(() => {
    if (isMobile && isAvailabilityModalOpen) {
      navigate(searchPath({ q: activity.title }));
    }
    scrollToElement(moreLikeThisRef.current || otherListingsRef.current);
  }, [
    navigate,
    scrollToElement,
    isMobile,
    isAvailabilityModalOpen,
    activity.title
  ]);

  const onClickViewTeacher = React.useCallback(() => {
    scrollToElement(viewTheTeacherRef?.current);
  }, [scrollToElement]);

  // tracking
  const trackClassPageImpression = useTrackClassPageImpression();
  useEffect(() => {
    trackClassPageImpression({
      activityUid: activity.uid,
      sectionUid: querySectionUid
    });
  }, [activity.uid, querySectionUid, trackClassPageImpression]);

  const onEnrollClick = React.useCallback(
    ({
      startTime,
      orgTeacherUid
    }: {
      startTime: Date;
      orgTeacherUid?: string;
    }) => {
      navigate(
        oneOnOneEnrollPath({
          activity,
          startTime,
          orgTeacherUid,
          isPrepayMeetings
        })
      );
    },
    [navigate, activity, isPrepayMeetings]
  );

  const [activityWithEnrollableTimes, setActivityWithEnrollableTimes] =
    React.useState<ActivityEnrollableTimesModal_ActivityFragment | undefined>(
      undefined
    );

  const esaState = getEsaState();
  const esaWaitPeriod = getEsaWaitPeriod();
  const fetchStartAfter = React.useMemo(() => {
    return !!esaState ? dayjs().add(esaWaitPeriod, "days").toDate() : undefined;
  }, [esaState, esaWaitPeriod]);

  const fetchEnrollableTimes = useFetchEnrollableTimes({
    startAfter: fetchStartAfter
  });

  React.useEffect(() => {
    if (activity.hasTeacherSchedule) {
      fetchEnrollableTimes(activity.uid).then(a => {
        setActivityWithEnrollableTimes(a);
      });
    }
  }, [activity, fetchEnrollableTimes]);

  const loadingOneOnOneActivity = !activityWithEnrollableTimes;
  const hasOneOnOneAvailabilities =
    !!activityWithEnrollableTimes?.enrollableTimes.length;

  const { data, loading: loadingRandomPopularCategories } =
    useRandomCategoriesByFacetQuery("popular", 20);
  const randomPopularCategories = React.useMemo(() => {
    if (loadingRandomPopularCategories) {
      return [];
    }

    // @ts-ignore TS(2345): Argument of type '(c: CategoryPage) => { title: st... Remove this comment to see the full error message
    return data?.randomCategoriesByFacet?.map((c: CategoryPage) => {
      return {
        title: c.nameTitleCased,
        basePath: c.basePath,
        slug: c.slug
      };
    });
  }, [data?.randomCategoriesByFacet, loadingRandomPopularCategories]);

  const memberPlanDiscountTooltip = React.useMemo(() => {
    return currentUser ? <MemberPlanDiscountTooltip /> : undefined;
  }, [currentUser]);

  const MobileSeeScheduleButtonMemo = React.useMemo(() => {
    if (activity.isClub && activity.canEnroll) {
      return (
        <SectionCallToAction
          activity={activity}
          section={activity.clubSection}
          isOrganizationEnrollment={isOrganizationEnrollment}
          trackingSharedProperties={{
            component: "MobileStickyFooter"
          }}
        />
      );
    }

    return activity.hasTeacherSchedule ? (
      <>
        <ClassScheduleRequestModal
          activity={activity}
          user={currentUser}
          // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
          userTimeZone={userTimeZone}
          open={openClassScheduleRequest}
          setOpen={setOpenClassScheduleRequest}
        />
        {!loadingOneOnOneActivity && (
          <ActivityEnrollableTimesCta
            onRequestAnotherTimeClick={() => setOpenClassScheduleRequest(true)}
            onClickViewAvailability={onClickViewAvailability}
            hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
            shouldHideAlert
          />
        )}
      </>
    ) : (
      <>
        <TrackedButton
          variant="contained"
          trackingName={"see_schedule_mobile_nav"}
          trackingUniqueId={activity.uid}
          // @ts-ignore TS(2554): Expected 0 arguments, but got 1.
          onClick={onClickSeeSchedule}
          data-test-id="see-schedule-button"
        >
          {ctaText}
        </TrackedButton>
      </>
    );
  }, [
    activity,
    onClickSeeSchedule,
    userTimeZone,
    currentUser,
    openClassScheduleRequest,
    setOpenClassScheduleRequest,
    onClickViewAvailability,
    loadingOneOnOneActivity,
    hasOneOnOneAvailabilities,
    ctaText,
    isOrganizationEnrollment
  ]);

  const showAvailableTimes = React.useMemo(() => {
    return (
      activity.isSelfPaced ||
      !(activity.isClub || activity.hasTeacherSchedule || isMobile)
    );
  }, [activity, isMobile]);

  const showDefaultClassDetail = !isMobile && showDetails && !activity.isClub;

  const prepayType = isOrganizationAdminWhoCanEnroll(currentUser)
    ? PrepayType.CharterAdmin
    : isPrepayMeetings === true && !!User.isAdmin(currentUser)
    ? PrepayType.CharterPO
    : isClassWalletSession
    ? PrepayType.ClassWallet
    : null;

  const isShowOldTutoringFlow =
    prepayType === PrepayType.CharterAdmin ||
    prepayType === PrepayType.CharterPO ||
    prepayType === PrepayType.ClassWallet;

  const tutorEnrollButtonBasicUsers = isShowOldTutoringFlow
    ? undefined
    : TutorEnrollButtonAndModal;

  return (
    <SimpleLayout {...props} allowCategoriesNavbar>
      {/* @ts-ignore TS(2322): Type '{ __typename?: "Section" | undefined; uid: s... Remove this comment to see the full error message */}
      <ActivityMetaTags activity={activity} />
      {currentUser && (canManage || canApprove) && (
        <Toolbar>
          <Container sx={{ marginBottom: "medium" }}>
            <Box
              flex
              sx={{
                alignItems: "center",
                justifyContent: "center",
                marginRight: "2em"
              }}
            >
              <ActivityStatusLabel activity={activity} />
              <Space x="small" />
              {canManage ? (
                <Link
                  to={
                    activity.isClub
                      ? clubManageListingPath(activity)
                      : activitySchedulePath(activity)
                  }
                >
                  {t`Manage this class`}
                </Link>
              ) : canApprove ? (
                <Link to={activityApprovalPath(activity)}>
                  {t`Approve this class`}
                </Link>
              ) : (
                ""
              )}
            </Box>
          </Container>
        </Toolbar>
      )}
      {!activity.canEnroll && !activity.isSelfPaced && (
        <Box ref={moreLikeThisRef} key="moreLikeThis">
          <SimilarListingsMiniTiles activity={activity} />
        </Box>
      )}
      {!isRestricted && (
        <>
          {isMobile && (
            <MobileStickyFooter
              activity={activity}
              button={MobileSeeScheduleButtonMemo}
              hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
              TutorEnrollButton={tutorEnrollButtonBasicUsers}
              activityWithEnrollableTimes={activityWithEnrollableTimes}
              leader={leader}
              MessageModal={MessageModal}
            />
          )}
          {isMobile &&
            activity.upcomingAvailableSectionCount === 0 &&
            activity.upcomingSectionCount === 0 && (
              <ClassScheduleRequestModal
                activity={activity}
                user={currentUser}
                // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
                userTimeZone={userTimeZone}
                open={openClassScheduleRequest}
                setOpen={setOpenClassScheduleRequest}
              />
            )}
          <ClassDetailsMainBody
            // data
            activity={activity}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
            leader={leader}
            canEnroll={activity.canEnroll}
            isOrganizationEnrollment={isOrganizationEnrollment}
            isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
            isLoadingOneOnOneAvailabilities={loadingOneOnOneActivity}
            hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
            facebookAppId={Env.FACEBOOK_APP_ID}
            showAvailableTimes={showAvailableTimes}
            // child components
            SectionCallToAction={SectionCallToAction}
            ChooseATimeOrClassScheduleRequestButton={
              ChooseATimeOrClassScheduleRequestButton
            }
            MessageModal={MessageModal}
            FollowButton={FollowButton}
            ReviewRespondButton={ReviewRespondButton}
            SeeScheduleButtonMemo={MobileSeeScheduleButtonMemo}
            TutorEnrollButton={tutorEnrollButtonBasicUsers}
            MemberPlanDiscountTooltip={memberPlanDiscountTooltip}
            // scroll refs
            sectionsRef={sectionsRef}
            viewAvailabilityRef={viewAvailabilityRef}
            viewTheTeacherRef={viewTheTeacherRef}
            // event handlers
            onClickMoreLikeThis={onClickMoreLikeThis}
            onClickViewTeacher={onClickViewTeacher}
            onEnrollClick={onEnrollClick}
            isAvailabilityModalOpen={isAvailabilityModalOpen}
            setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
          />
          <Switch>
            <Route
              exact
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : activity.hasTeacherSchedule ? (
                  <EnrollmentMeetingPurchasePage activityUid={activity.uid} />
                ) : isClassWalletSession ? (
                  <ActivityClassWalletEnrollPage activity={activity} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/waitlist`}
              render={routeProps => {
                const redirectPath = appState.getWaitlistPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityWaitlistPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/charter-enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityCharterSchoolEnrollPage activity={activity} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/email`}
              render={routeProps => {
                const redirectPath = appState.requireAuth(routeProps.location);

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityEmailPreview {...props} {...routeProps} />
                );
              }}
            />
            {!!currentUser && (
              <Route
                render={routeProps => {
                  const querySectionUid =
                    routeProps.location?.query?.sectionUid;
                  if (!!currentUser && querySectionUid && props.activity) {
                    const enrollPath = isOrganizationEnrollment
                      ? sectionCharterSchoolEnrollPath(
                          props.activity,
                          querySectionUid
                        )
                      : sectionEnrollPath(props.activity, querySectionUid);
                    return <Redirect to={enrollPath} />;
                  }
                  return null;
                }}
              />
            )}
          </Switch>
        </>
      )}
      {showDefaultClassDetail && (
        <DefaultBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
          moreLikeThisRef={moreLikeThisRef}
          isRestricted={isRestricted}
          randomPopularCategories={randomPopularCategories}
        />
      )}

      {isMobile && (
        <MobileBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
        />
      )}
    </SimpleLayout>
  );
}
